import { useEffect, useRef, useState } from 'react'
import { useOrganization, useOrganizationList } from '@clerk/clerk-react'
import { useQueryClient } from '@tanstack/react-query'
import { BypassPermissionsRow } from '@features/permissions/components/BypassPermissionsRow'
import { usePermissionBypassStore } from '@features/permissions/stores/usePermissionBypassStore'
import { useSettingsStore } from '@features/settings/stores/useSettingsStore'
import { DropdownMenu } from '@features/shared/components/DropdownMenu'
import { ChevronRightIcon } from '@features/shared/icons/icons'
import { MenuDescription } from '@features/sidebar/components/MenuDescription'

/** Account context controls, separate from the page links below them. */
export function AccountContextSettings({ onInteract }: { onInteract?: () => void }) {
  const { organization } = useOrganization()
  const { isLoaded, setActive, userMemberships } = useOrganizationList({
    userMemberships: { infinite: true, pageSize: 100, keepPreviousData: true },
  })
  const isAdministrator = useSettingsStore((state) => state.isAdministrator)
  const [switching, setSwitching] = useState(false)
  const [switchError, setSwitchError] = useState(false)
  const [organizationMenuOpen, setOrganizationMenuOpen] = useState(false)
  const organizationRowRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()
  const { hasNextPage, isFetching, fetchNext } = userMemberships

  useEffect(() => {
    if (hasNextPage && !isFetching) void fetchNext()
  }, [hasNextPage, isFetching, fetchNext])

  const options = (userMemberships.data ?? []).map(({ organization: memberOrganization }) => ({
    id: memberOrganization.id,
    label: memberOrganization.name,
  }))
  if (organization && !options.some((option) => option.id === organization.id)) {
    options.unshift({ id: organization.id, label: organization.name })
  }
  const organizationName = organization?.name ?? '—'

  const switchOrganization = async (organizationId: string) => {
    if (!isLoaded || !setActive || switching || organizationId === organization?.id) return
    onInteract?.()
    setSwitching(true)
    setSwitchError(false)
    // Access elevation never carries into another organization. The existing
    // identity boundary retires the previous organization's caches and stores.
    usePermissionBypassStore.getState().setBypassing(false)
    try {
      await setActive({ organization: organizationId })
    } catch {
      setSwitchError(true)
      // Bypass has already been switched off; reconcile the current account's
      // visible data even if the organization change couldn't complete.
      void queryClient.invalidateQueries()
    } finally {
      setSwitching(false)
    }
  }

  return (
    <div className="shrink-0 space-y-[var(--sidebar-item-inset)] border-b border-[color:var(--sidebar-submenu-border)] p-[var(--sidebar-item-inset)]">
      <div
        ref={organizationRowRef}
        className="flex h-7 min-w-0 items-center gap-2 pl-2 pr-1.5 text-xs font-normal text-[color:var(--sidebar-submenu-text-muted)]"
      >
        <span>Organization</span>
        <MenuDescription description="The organization you're working in." />
        <DropdownMenu
          wrapperClassName="ml-auto min-w-0 max-w-[50%]"
          open={organizationMenuOpen}
          onOpenChange={(open) => {
            setOrganizationMenuOpen(open)
            if (open) onInteract?.()
          }}
          placement="right-start"
          alignFirstItemToAnchor
          anchorElementRef={organizationRowRef}
          sections={[
            {
              items: options.map((option) => ({
                type: 'option',
                id: option.id,
                label: option.label,
                selected: option.id === organization?.id,
                disabled: switching,
                onSelect: () => void switchOrganization(option.id),
              })),
            },
          ]}
          trigger={
            <button
              type="button"
              aria-label={`Organization: ${organizationName}`}
              aria-haspopup="menu"
              aria-expanded={organizationMenuOpen}
              disabled={switching || !isLoaded}
              className="flex max-w-full items-center gap-1 rounded px-1 py-0.5 text-[color:var(--sidebar-submenu-text-data)] outline-none hover:bg-[color:var(--sidebar-item-hover-bg)] focus-visible:ring-2 focus-visible:ring-[color:var(--accent-info)]"
            >
              <span className="truncate">{organizationName}</span>
              <ChevronRightIcon size={12} className="shrink-0" aria-hidden="true" />
            </button>
          }
        />
      </div>
      {switchError && (
        <p role="alert" className="px-2 text-xs text-danger">
          Couldn't switch organization. Please try again.
        </p>
      )}
      {isAdministrator && (
        <>
          <div
            aria-hidden="true"
            className="mx-2 border-t border-[color:var(--sidebar-settings-divider)]"
          />
          <div className="flex h-7 min-w-0 items-center gap-2 pl-2 pr-1.5 text-xs font-normal text-[color:var(--sidebar-submenu-text-muted)]">
            <span aria-disabled="true">Impersonate</span>
            <MenuDescription description="See the app with another user's permissions. Coming soon; not available yet." />
            <span className="ml-auto inline-flex h-5 shrink-0 items-center rounded border border-[color:var(--border-strong)] bg-muted px-2 text-[10px] font-normal leading-none text-[color:var(--sidebar-submenu-text-faint)]">
              Coming Soon
            </span>
          </div>
          <div
            aria-hidden="true"
            className="mx-2 border-t border-[color:var(--sidebar-settings-divider)]"
          />
        </>
      )}
      <BypassPermissionsRow onToggle={onInteract} />
    </div>
  )
}
